import { useAuth } from "@/providers";
import { type DatasetRecord } from "@/shared/domain/datasets";
import { useDatasetsQuery } from "@/shared/domain/datasets/hooks";
import { Combobox, Field } from "@/shared/foundation/primitives";

export interface DatasetPickerProps {
  /** The chosen dataset's id, or null. */
  value: string | null;
  onValueChange: (datasetId: string | null) => void;
  /** A visible label; omit it when a heading names the picker instead. */
  label?: string;
  /** Id of the heading that names the picker when it has no label of its own. */
  "aria-labelledby"?: string;
  error?: string;
  disabled?: boolean;
}

function datasetLabel(dataset: DatasetRecord): string {
  return typeof dataset.name === "string" && dataset.name.length > 0
    ? `${dataset.name} (${dataset.dataset_id})`
    : dataset.dataset_id;
}

/**
 * One dataset out of the org's, searchable.
 *
 * A dataset the query has not returned yet — a stored id, or one further down
 * a long list — still shows as its own id rather than as an empty field.
 */
export function DatasetPicker({
  value,
  onValueChange,
  label,
  "aria-labelledby": ariaLabelledBy,
  error,
  disabled,
}: DatasetPickerProps) {
  const { currentOrganization } = useAuth();
  const datasetsQuery = useDatasetsQuery({
    orgId: currentOrganization?.org_id,
    query: { sort_by: "created", sort_direction: "DESC" },
  });
  const datasets = datasetsQuery.data ?? [];
  const known = datasets.some((dataset) => dataset.dataset_id === value);
  const items =
    value === null || known
      ? datasets
      : [{ dataset_id: value, name: null } as DatasetRecord, ...datasets];

  return (
    <Field.Root invalid={error !== undefined} disabled={disabled}>
      <Combobox<DatasetRecord>
        items={items}
        value={items.find((dataset) => dataset.dataset_id === value) ?? null}
        onValueChange={(dataset) => {
          onValueChange(dataset === null ? null : dataset.dataset_id);
        }}
        itemToStringLabel={datasetLabel}
        isItemEqualToValue={(item, chosen) =>
          item.dataset_id === chosen.dataset_id
        }
        label={label}
        disabled={disabled}
        size="lg"
      >
        <Combobox.TriggerInput
          placeholder="Search datasets"
          aria-labelledby={ariaLabelledBy}
        />
        <Combobox.Content>
          <Combobox.Empty />
          <Combobox.List>
            {(dataset: DatasetRecord) => (
              <Combobox.Item key={dataset.dataset_id} value={dataset} />
            )}
          </Combobox.List>
        </Combobox.Content>
      </Combobox>
      <Field.Error match>{error}</Field.Error>
    </Field.Root>
  );
}
